import { Link } from "react-router";
import clsx from "clsx";
import { Badge, Button } from "../../ui";
import {
  EyeIcon,
  UserIcon,
  TrashIcon,
  CheckBadgeIcon,
  MapPinIcon,
  FireIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  CalendarIcon,
  PhoneIcon,
  EnvelopeIcon
} from "@heroicons/react/24/outline";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from "@headlessui/react";
import { Fragment } from "react";
import { StageDisplay } from "../leadTable/StageDisplay";
import { PriorityIndicator } from "../leadTable/PriorityIndicator";
import { formatLeadName } from "../../../utils/leadNameFormatter";
import { hasPermission } from "utils/permissionsUtils";
import { getLocalDateTime, formatDate, formatTime } from "../../../utils/dateUtils";

const getStatusColors = (status) => {
  const normalizedStatus = status?.toLowerCase()?.trim();
  switch (normalizedStatus) {
    case "pending":
      return {
        ring: "ring-yellow-400",
        bg: "from-yellow-100 to-yellow-200 dark:from-yellow-900/30 dark:to-yellow-800/30",
        text: "text-yellow-700 dark:text-yellow-300",
      };
    case "completed":
      return {
        ring: "ring-blue-400",
        bg: "from-blue-100 to-blue-200 dark:from-blue-900/30 dark:to-blue-800/30",
        text: "text-blue-700 dark:text-blue-300",
      };
    case "missed":
      return {
        ring: "ring-orange-400",
        bg: "from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30",
        text: "text-orange-700 dark:text-orange-300",
      };
    case "failed":
      return {
        ring: "ring-red-400",
        bg: "from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30",
        text: "text-red-700 dark:text-red-300",
      };
    case "contacted":
      return {
        ring: "ring-green-400",
        bg: "from-green-100 to-green-200 dark:from-green-900/30 dark:to-green-800/30",
        text: "text-green-700 dark:text-green-300",
      };
    case "closed":
      return {
        ring: "ring-emerald-400",
        bg: "from-emerald-100 to-emerald-200 dark:from-emerald-900/30 dark:to-emerald-800/30",
        text: "text-emerald-700 dark:text-emerald-300",
      };
    case "canceled":
      return {
        ring: "ring-gray-400",
        bg: "from-gray-100 to-gray-200 dark:from-gray-900/30 dark:to-gray-800/30",
        text: "text-gray-700 dark:text-gray-300",
      };
    default:
      return {
        ring: "ring-gray-400",
        bg: "from-gray-100 to-gray-200 dark:from-gray-900/30 dark:to-gray-800/30",
        text: "text-gray-700 dark:text-gray-300",
      };
  }
};

const getStatusDotColor = (status) => {
  const normalizedStatus = status?.toLowerCase()?.trim();
  return normalizedStatus === "pending"
    ? "bg-yellow-400"
    : normalizedStatus === "completed"
      ? "bg-blue-400"
      : normalizedStatus === "contacted"
        ? "bg-green-400"
        : normalizedStatus === "missed"
          ? "bg-orange-400"
          : normalizedStatus === "failed"
            ? "bg-red-400"
            : normalizedStatus === "closed"
              ? "bg-emerald-400"
              : normalizedStatus === "canceled"
                ? "bg-gray-400"
                : "bg-gray-300";
};

export function DuplicateLeadCard({
  duplicate,
  isManager = false,
  onCreateUser,
  onDelete,
  isCreatingUser = false,
  viewOnly = false,
}) {
  const leadDetails = duplicate?.DuplicateLeadDetails?.[0] || {};
  const duplicateLeadId = duplicate?.DuplicateLeadId;
  const statusName = leadDetails?.StatusName || leadDetails?.status;
  const colors = getStatusColors(statusName);
  const dotColor = getStatusDotColor(statusName);
  const hasDeletePermission = isManager ? hasPermission("MLLD") : hasPermission("APPLIDELETE");

  return (
    <div className="rounded-lg border border-amber-200/60 bg-gradient-to-br from-amber-50/40 to-amber-50/20 p-4 dark:border-amber-500/20 dark:from-dark-700/60 dark:to-dark-700/40">
      {/* Header with Avatar and Basic Info */}
      <div className="mb-4 flex items-start gap-4">
        {/* Avatar */}
        <div className="relative shrink-0">
          <div
            className={`flex size-12 items-center justify-center rounded-full bg-gradient-to-br ring-2 ${colors.ring} ${colors.bg}`}
          >
            <span className={`text-sm font-semibold ${colors.text}`}>
              {(leadDetails?.FullName || leadDetails?.name)
                ?.charAt(0)
                ?.toUpperCase() || "?"}
            </span>
          </div>

          {/* Status Dot */}
          <div
            className={`absolute -top-0.5 -left-0.5 flex size-3 items-center justify-center rounded-full ${dotColor} ring-2 ring-white dark:ring-dark-700`}
            title={`Status: ${statusName}`}
          />

          {/* Hot/Untouched Indicators */}
          {leadDetails?.isHot && (
            <div className="absolute -top-1 -right-1 flex size-4 items-center justify-center rounded-full bg-red-500">
              <FireIcon className="size-2.5 text-white" />
            </div>
          )}
          {leadDetails?.isUntouched && (
            <div className="absolute -right-1 -bottom-1 flex size-4 items-center justify-center rounded-full bg-red-500">
              <ExclamationTriangleIcon className="size-2.5 text-white" />
            </div>
          )}
        </div>

        {/* Name and ID */}
        <div className="min-w-0 flex-1">
          <Link
            to={
              isManager
                ? `/leads/manager/${leadDetails?.LeadId || leadDetails?.id}`
                : `/application/details/${leadDetails?.LeadId || leadDetails?.id}`
            }
            className="hover:text-primary-600 dark:hover:text-primary-400 group transition-colors"
          >
            <h4 className="dark:text-dark-50 flex items-center flex-wrap gap-2 group-hover:text-primary-600 dark:group-hover:text-primary-400 truncate text-sm font-medium text-gray-900">
              {formatLeadName(leadDetails)}
            </h4>
          </Link>
          {/* <p className="text-xs text-gray-500 dark:text-dark-400">
            ID: <span className="font-medium text-gray-700 dark:text-dark-200">#{leadDetails?.DisplayLeadId}</span>
          </p> */}
          {duplicate?.MatchType && (
            <Badge color="info" variant="soft" className="mt-1 text-[10px] font-medium">
              {duplicate.MatchType}
            </Badge>
          )}
        </div>


        <Menu as="div" className="relative shrink-0">
          <MenuButton className="flex items-center cursor-pointer justify-center rounded-lg p-1.5 text-gray-600 transition hover:bg-amber-100 dark:text-dark-300 dark:hover:bg-amber-500/20">
            <span className="text-lg">⋮</span>
          </MenuButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-100"
            enterFrom="transform opacity-0 scale-95"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-2"
          >
            <MenuItems
              anchor="bottom end"
              portal
              className="dark:bg-dark-750 dark:border-dark-500 z-[130] min-w-[10rem] rounded-lg border border-gray-300 bg-white py-1 shadow-lg shadow-gray-200/50 outline-hidden focus-visible:outline-hidden dark:shadow-none"
            >
              <MenuItem>
                {({ focus }) => (
                  <Link
                    to={
                      isManager
                        ? `/leads/manager/${leadDetails?.LeadId}`
                        : `/application/details/${leadDetails?.LeadId}`
                    }
                    className={clsx(
                      "flex h-9 w-full items-center space-x-3 px-3 tracking-wide outline-hidden transition-colors",
                      focus &&
                      "dark:bg-dark-600 dark:text-dark-100 bg-gray-100 text-gray-800",
                    )}
                  >
                    <EyeIcon className="size-4.5 stroke-1" />
                    <span>View Details</span>
                  </Link>
                )}
              </MenuItem>

              {hasDeletePermission && !viewOnly && (
                <MenuItem>
                  {({ focus }) => (
                    <button
                      onClick={() => onDelete?.(leadDetails?.LeadId)}
                      className={clsx(
                        "this:error text-this dark:text-this-light flex h-9 w-full items-center space-x-3 px-3 tracking-wide outline-hidden transition-colors",
                        focus && "bg-this/10 dark:bg-this-light/10",
                      )}
                    >
                      <TrashIcon className="size-4.5 stroke-1" />
                      <span>Delete Lead</span>
                    </button>
                  )}
                </MenuItem>
              )}
            </MenuItems>
          </Transition>
        </Menu>

      </div>

      {/* Details Grid */}
      <div className="space-y-3">
        {/* Row 1: Stage, Status, Medium */}
        <div className="grid gap-3 sm:grid-cols-3">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Stage</p>
            <div className="mt-1">
              {leadDetails?.StageName || leadDetails?.StageId ? (
                <StageDisplay
                  stageName={leadDetails?.StageName}
                  stageId={leadDetails?.StageId}
                />
              ) : (
                <span className="text-xs text-gray-400 dark:text-dark-500">--</span>
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Status</p>
            <p className="mt-1 text-xs font-medium text-gray-700 dark:text-dark-200">
              {statusName || "--"}
            </p>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Medium</p>
            <p className="mt-1 text-xs text-gray-600 dark:text-dark-300">
              {leadDetails?.Medium || "--"}
            </p>
          </div>
        </div>

        {/* Row 2: Priority & Score */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Priority</p>
            <div className="mt-1">
              <PriorityIndicator lead={leadDetails} />
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Lead Score</p>
            <div className="mt-1 flex items-center gap-2">
              <div className="h-1.5 flex-1 rounded-full bg-gray-200 dark:bg-dark-600">
                <div
                  className={clsx(
                    "h-1.5 rounded-full transition-all",
                    leadDetails?.scoreProgressColor || "bg-gray-300",
                  )}
                  style={{
                    width: `${Math.min(leadDetails?.LeadScore || leadDetails?.leadScore || 0, 100)}%`,
                  }}
                />
              </div>
              <span className="min-w-[2rem] text-xs font-medium text-gray-600 dark:text-dark-300">
                {(leadDetails?.LeadScore || leadDetails?.leadScore) === "Unscored"
                  ? "--"
                  : leadDetails?.LeadScore || leadDetails?.leadScore || 0}
              </span>
            </div>
          </div>
        </div>

        {/* Row 3: Contact Info */}
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Email</p>
            <div className="mt-1 flex items-center gap-1">
              <span className="truncate text-xs text-gray-700 dark:text-dark-200">
                {leadDetails?.Email || leadDetails?.email || "--"}
              </span>
              {leadDetails?.EmailVerified ? (
                <CheckBadgeIcon className="size-3.5 shrink-0 text-green-500" />
              ) : (
                <XCircleIcon className="text-red-500 inline size-4" />
              )}
            </div>
          </div>

          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Phone</p>
            <div className="mt-1 flex items-center gap-1">
              <span className="truncate text-xs text-gray-700 dark:text-dark-200">
                {leadDetails?.PhoneNumber || leadDetails?.mobile || leadDetails?.phone || "--"}
              </span>
              {leadDetails?.PhoneVerified ? (
                <CheckBadgeIcon className="size-3.5 shrink-0 text-green-500" />
              ) : (
                <XCircleIcon className="text-red-500 inline size-4" />
              )}
            </div>
          </div>
        </div>

        {/* Row 4: Location */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Location</p>
          <div className="flex items-start gap-2">
            <MapPinIcon className="mt-0.5 size-4 shrink-0 text-gray-400" />
            <div className="dark:text-dark-300 min-w-0 text-sm text-gray-600">
              <div className="font-medium">
                {leadDetails?.City || leadDetails?.city || "N/A"}
              </div>
              <div className="dark:text-dark-400 truncate text-xs text-gray-500">
                {leadDetails?.Country || "N/A"}, {leadDetails?.State || leadDetails?.state || "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* Row 5: CreatedAt */}
        <div>
          <p className="text-xs font-medium text-gray-500 dark:text-dark-400">Created At</p>
          <div className="flex items-start gap-2">
            <CalendarIcon className="mt-0.5 size-4 shrink-0 text-gray-400" />
            <div className="dark:text-dark-300 min-w-0 text-sm text-gray-600">
              <div className="font-medium">
                {leadDetails?.CreatedAt ? formatDate(leadDetails.CreatedAt) : "N/A"}
              </div>
              <div className="dark:text-dark-400 truncate text-xs text-gray-500">
                {leadDetails?.CreatedAt ? formatTime(leadDetails.CreatedAt) : "N/A"}
              </div>
            </div>
          </div>
        </div>

        {/* User Account Status */}
        {!leadDetails?.LeadUserId && (
          <div className="rounded-md bg-red-50/60 p-2 dark:bg-red-900/20">
            <p className="inline-flex items-center gap-1.5 text-xs font-medium text-red-600 dark:text-red-300">
              <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
              User account not created
            </p>
          </div>
        )}

      </div>
    </div>
  );
}
